import * as bitcoin from 'bitcoinjs-lib'
import * as btc from '@scure/btc-signer'
import { SecurePurchaseUnavailableError } from './secure-purchase'

type FundingScriptType = 'p2tr' | 'p2wpkh' | 'p2sh-p2wpkh'

export function classifyOutputScript(script: Buffer | Uint8Array): FundingScriptType {
	const bytes = Buffer.from(script)
	if (bytes.length === 34 && bytes[0] === 0x51 && bytes[1] === 0x20) {
		return 'p2tr'
	}
	if (bytes.length === 22 && bytes[0] === 0x00 && bytes[1] === 0x14) {
		return 'p2wpkh'
	}
	if (bytes.length === 23 && bytes[0] === 0xa9 && bytes[1] === 0x14 && bytes[22] === 0x87) {
		return 'p2sh-p2wpkh'
	}
	throw new SecurePurchaseUnavailableError(
		'unsupported_script',
		'Seller/buyer funding must be P2TR, native P2WPKH, or nested P2SH-P2WPKH'
	)
}

const BITCOIN_NETWORK = bitcoin.networks.bitcoin

export function parsePsbt(hex: string): bitcoin.Psbt {
	try {
		return bitcoin.Psbt.fromHex(hex, { network: BITCOIN_NETWORK })
	} catch {
		const tx = btc.Transaction.fromPSBT(Buffer.from(hex, 'hex'), { allowLegacyWitnessUtxo: true })
		return bitcoin.Psbt.fromHex(Buffer.from(tx.toPSBT()).toString('hex'), {
			network: BITCOIN_NETWORK
		})
	}
}

function inputScript(psbt: bitcoin.Psbt, index: number): Buffer {
	const data = psbt.data.inputs[index]
	if (data.witnessUtxo?.script) {
		return Buffer.from(data.witnessUtxo.script)
	}
	const tx = data.nonWitnessUtxo
		? bitcoin.Transaction.fromBuffer(Buffer.from(data.nonWitnessUtxo))
		: null
	const prevout = tx?.outs[psbt.txInputs[index].index]
	if (prevout?.script) {
		return Buffer.from(prevout.script)
	}
	throw new SecurePurchaseUnavailableError(
		'missing_witness_utxo',
		'PSBT input is missing prevout script'
	)
}

export function classifyPsbtInput(psbt: bitcoin.Psbt, index = 0): FundingScriptType {
	return classifyOutputScript(inputScript(psbt, index))
}

function readVarInt(buffer: Buffer, offset: number): { value: number; size: number } {
	const first = buffer[offset]
	if (first < 0xfd) return { value: first, size: 1 }
	if (first === 0xfd) return { value: buffer.readUInt16LE(offset + 1), size: 3 }
	if (first === 0xfe) return { value: buffer.readUInt32LE(offset + 1), size: 5 }
	return { value: Number(buffer.readBigUInt64LE(offset + 1)), size: 9 }
}

function witnessItems(finalScriptWitness: Buffer | Uint8Array): Buffer[] {
	const buffer = Buffer.from(finalScriptWitness)
	const items: Buffer[] = []
	let offset = 0
	const count = readVarInt(buffer, offset)
	offset += count.size
	for (let i = 0; i < count.value; i++) {
		const length = readVarInt(buffer, offset)
		offset += length.size
		items.push(buffer.subarray(offset, offset + length.value))
		offset += length.value
	}
	return items
}

/**
 * The sighash byte the signer actually committed to for one input, read from
 * the signature itself. A 64-byte Schnorr signature means SIGHASH_DEFAULT
 * (0x00); a 65-byte one carries its sighash as the trailing byte, as does a
 * DER ECDSA signature. Returns undefined when the input carries no signature.
 */
type PsbtInputData = bitcoin.Psbt['data']['inputs'][number]

export function signedInputSighash(input: PsbtInputData): number | undefined {
	if (input.tapKeySig && input.tapKeySig.length) {
		return input.tapKeySig.length === 65 ? input.tapKeySig[64] : bitcoin.Transaction.SIGHASH_DEFAULT
	}
	if (input.partialSig?.length) {
		const signature = Buffer.from(input.partialSig[0].signature)
		return signature[signature.length - 1]
	}
	if (input.finalScriptWitness && input.finalScriptWitness.length) {
		const items = witnessItems(input.finalScriptWitness)
		const signature = items[0]
		if (!signature || signature.length === 0) return undefined
		if (items.length === 1) {
			// Taproot key-path spend: a bare Schnorr signature.
			return signature.length === 65 ? signature[64] : bitcoin.Transaction.SIGHASH_DEFAULT
		}
		// P2WPKH / nested P2WPKH: <DER sig + sighash byte> <pubkey>.
		return signature[signature.length - 1]
	}
	return undefined
}

function outputScriptForAddress(address: string): Buffer {
	return bitcoin.address.toOutputScript(address, BITCOIN_NETWORK)
}

/** The unsigned transaction inside a PSBT, as hex, for display and pinning. */
export function unsignedTxHexFromPsbt(psbtHex: string): string {
	const psbt = parsePsbt(psbtHex)
	const tx = new bitcoin.Transaction()
	tx.version = psbt.version
	tx.locktime = psbt.locktime
	psbt.txInputs.forEach((input) =>
		tx.addInput(Buffer.from(input.hash), input.index, input.sequence)
	)
	psbt.txOutputs.forEach((output) => tx.addOutput(Buffer.from(output.script), output.value))
	return tx.toHex()
}

/**
 * The txid the completed transaction will have once the wallet signs it.
 *
 * A PSBT's unsigned transaction carries empty scriptSigs (BIP-174), but a
 * nested P2SH-P2WPKH input (Xverse's payment address type) spends with the
 * redeem-script push in its scriptSig, and the scriptSig is part of the
 * txid. The API derives and stores sale/setup txids from that completed
 * form, so the client must re-apply the push from the PSBT's `redeemScript`
 * before comparing. Native SegWit and Taproot inputs have empty scriptSigs
 * either way, so for those buyers this is exactly the unsigned txid.
 */
export function finalTxidFromPsbt(psbtHex: string): string {
	const psbt = parsePsbt(psbtHex)
	const tx = bitcoin.Transaction.fromHex(unsignedTxHexFromPsbt(psbtHex))
	psbt.data.inputs.forEach((data, index) => {
		if (!data.redeemScript || data.redeemScript.length === 0) return
		let nested = false
		try {
			nested = classifyOutputScript(inputScript(psbt, index)) === 'p2sh-p2wpkh'
		} catch {
			nested = false
		}
		if (nested) {
			tx.ins[index].script = bitcoin.script.compile([Buffer.from(data.redeemScript)])
		}
	})
	return tx.getId()
}

export function buyerInputIndexes(psbtHex: string, buyerScripts: Buffer[]): number[] {
	const psbt = parsePsbt(psbtHex)
	const indexes: number[] = []
	for (let i = 0; i < psbt.inputCount; i++) {
		try {
			const script = inputScript(psbt, i)
			if (buyerScripts.some((owned) => owned.equals(script))) {
				indexes.push(i)
			}
		} catch {
			// Skip inputs we cannot classify; the wallet adapter still signs owned inputs.
		}
	}
	return indexes
}

/**
 * Sighash type each listed input expects its signer to use, read from the
 * PSBT itself. Wallet adapters that take an explicit `toSignInputs` list
 * (UniSat, OYL) default to SIGHASH_ALL unless told otherwise and refuse the
 * 0x81 seller authorization, so callers pass this through.
 */
export function inputSighashTypes(
	psbtHex: string,
	indexes: number[],
	options: { taprootDefault?: boolean } = {}
): number[][] {
	const psbt = parsePsbt(psbtHex)
	return indexes.map((index) => [
		psbt.data.inputs[index]?.sighashType ??
			(options.taprootDefault && classifyPsbtInput(psbt, index) === 'p2tr'
				? bitcoin.Transaction.SIGHASH_DEFAULT
				: bitcoin.Transaction.SIGHASH_ALL)
	])
}

export function buyerScriptsForAddresses(addresses: string[]): Buffer[] {
	return addresses.filter(Boolean).map((address) => outputScriptForAddress(address))
}

export type SecureInvoiceBreakdown = {
	sellerProceedsSat: number
	marketFeeSat: number
	creatorRoyaltySat: number
	networkFeeSat: number
	changeSat: number
	itemTotalSat: number
	totalSat: number
}

export type SecurePurchaseItemBreakdown = {
	sellerProceedsSat: number
	marketFeeSat: number
	creatorRoyaltySat: number
	itemTotalSat: number
}
